import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FiSearch, FiX, FiExternalLink, FiPlus } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { PaginationControl } from "react-bootstrap-pagination-control";
import {
  GET_WISH_LIST_AUCTION,
  GET_WISH_LIST_BUYNOW,
  DELETE_WISH_LIST_ITEM,
} from "../../constant";
import { GET, POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { API_ENDPOINT } from "../../services/api";
import { getAuthUserId } from "../../services/localStorage";
import {
  sanitizeImageUrl,
  deepSanitizeImageUrls,
} from "../../utils/sanitizeImageUrl";
import ConfirmDialog from "../ConfirmDialog";
import DetailDrawer from "../DetailDrawer";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const TABS = [
  {
    key: "auction",
    name: "Auction",
    typeQuery: "type=auction",
    actionType: GET_WISH_LIST_AUCTION,
    listKey: "wishList",
    viewPath: "/dealers/dashbord/product",
  },
  {
    key: "buynow",
    name: "Buy now",
    typeQuery: "type=buynow",
    actionType: GET_WISH_LIST_BUYNOW,
    listKey: "wishListbuy",
    viewPath: "/dealers/dashbord/productbuy",
  },
];

function toNumber(value) {
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `€${num.toLocaleString()}`;
}

function matchesSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.product_name,
    item?.product_category,
    item?.product_description,
    item?.registration,
    item?.registration_number,
    item?.product_id,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function WatchlistActive({
  user_id,
  wishList,
  wishListbuy,
  loadingGetWishList,
  loadingGetWishListBuy,
  getWishList,
  deleteItem,
}) {
  const [tabIdx, setTabIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);

  const tab = TABS[tabIdx];
  const id = user_id || getAuthUserId();
  const sourceList = Array.isArray(
    tab.listKey === "wishListbuy" ? wishListbuy : wishList
  )
    ? tab.listKey === "wishListbuy"
      ? wishListbuy
      : wishList
    : [];

  const isLoading =
    tab.listKey === "wishListbuy"
      ? Boolean(loadingGetWishListBuy) && sourceList.length === 0
      : Boolean(loadingGetWishList) && sourceList.length === 0;

  useEffect(() => {
    if (!id) return;
    TABS.forEach((floor) => {
      getWishList(id, floor.typeQuery, floor.actionType);
    });
  }, [id]);

  useEffect(() => {
    if (!id) return;
    getWishList(id, tab.typeQuery, tab.actionType);
  }, [tabIdx]);

  useEffect(() => {
    setPage(1);
    setSearch("");
    setSortBy("name_asc");
    setPendingRemove(null);
    setViewingItem(null);
  }, [tabIdx]);

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, pageSize]);

  const filteredList = useMemo(() => {
    let rows = [...sourceList].filter((item) => matchesSearch(item, search));
    rows.sort((a, b) => {
      if (sortBy === "price_desc") {
        return toNumber(b?.product_price) - toNumber(a?.product_price);
      }
      if (sortBy === "price_asc") {
        return toNumber(a?.product_price) - toNumber(b?.product_price);
      }
      if (sortBy === "name_desc") {
        return String(b?.product_name || "").localeCompare(
          String(a?.product_name || "")
        );
      }
      return String(a?.product_name || "").localeCompare(
        String(b?.product_name || "")
      );
    });
    return rows;
  }, [sourceList, search, sortBy]);

  const pageCount = Math.max(1, Math.ceil(filteredList.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filteredList.length);
  const pageRows = filteredList.slice(pageStart, pageEnd);
  const hasActiveFilters = Boolean(search) || sortBy !== "name_asc";

  const clearFilters = () => {
    setSearch("");
    setSortBy("name_asc");
    setPage(1);
  };

  const confirmRemove = async () => {
    if (!pendingRemove || removing) return;
    setRemoving(true);
    const productId = pendingRemove.product_id;
    const index = pendingRemove.index;
    try {
      await fetch(API_ENDPOINT(Labels.close_wish_list), {
        method: POST,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: id,
          product: productId,
          product_index: index,
        }),
      })
        .then((r) => r.json())
        .then((json) => deepSanitizeImageUrls(json));
      deleteItem({ id: productId, listKey: tab.listKey });
    } finally {
      setRemoving(false);
      setPendingRemove(null);
    }
  };

  return (
    <div className="dd-section">
      <nav className="dd-tabs" aria-label="Active watchlist sections">
        {TABS.map((item, index) => {
          const count =
            index === 0
              ? Array.isArray(wishList)
                ? wishList.length
                : 0
              : Array.isArray(wishListbuy)
              ? wishListbuy.length
              : 0;
          return (
            <button
              key={item.key}
              type="button"
              className={`dd-tab${index === tabIdx ? " is-active" : ""}`}
              onClick={() => setTabIdx(index)}
            >
              {item.name}
              <span className="dd-tab__count">{count}</span>
            </button>
          );
        })}
      </nav>

      {isLoading ? (
        <div className="dd-empty" aria-busy="true">
          <strong>Loading watchlist…</strong>
          <p>Fetching active {tab.name.toLowerCase()} lots on your desk.</p>
        </div>
      ) : (
        <>
          <div className="dd-toolbar">
            <label className="dd-toolbar__search">
              <FiSearch size={16} aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, category, reg…"
                aria-label="Search watchlist"
              />
              {search ? (
                <button
                  type="button"
                  className="dd-toolbar__clear-input"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              ) : null}
            </label>

            <label className="dd-toolbar__field">
              <span>Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name_asc">Name · A–Z</option>
                <option value="name_desc">Name · Z–A</option>
                <option value="price_desc">Price · high to low</option>
                <option value="price_asc">Price · low to high</option>
              </select>
            </label>

            <label className="dd-toolbar__field">
              <span>Per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            {hasActiveFilters ? (
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={() => {
                  setSearch("");
                  setSortBy("name_asc");
                  setPage(1);
                }}
              >
                Reset
              </button>
            ) : null}

            <Link
              to="/dealers/dashbord/watchlist"
              className="dd-btn dd-btn--primary"
            >
              <FiPlus size={16} aria-hidden />
              Add lots
            </Link>
          </div>

          <div className="dd-toolbar__meta">
            {filteredList.length ? (
              <span>
                Showing {pageStart + 1}–{pageEnd} of {filteredList.length}
                {filteredList.length !== sourceList.length
                  ? ` (filtered from ${sourceList.length})`
                  : ""}
              </span>
            ) : (
              <span>No matching watchlist items</span>
            )}
          </div>

          {!sourceList.length ? (
            <div className="dd-empty">
              <strong>No active {tab.name.toLowerCase()} watchlist</strong>
              <p>
                Add lots from the trade floor and they will stay here while you
                track them.
              </p>
              <Link
                to="/dealers/dashbord/watchlist"
                className="dd-btn dd-btn--primary"
                style={{ marginTop: "0.75rem" }}
              >
                <FiPlus size={16} aria-hidden />
                Add watchlist lots
              </Link>
            </div>
          ) : !filteredList.length ? (
            <div className="dd-empty">
              <strong>No matches</strong>
              <p>Try a different search or reset filters.</p>
            </div>
          ) : (
            <>
              <div className="dd-table-wrap dd-wl-table">
                <table className="dd-table">
                  <thead>
                    <tr>
                      <th scope="col">Vehicle</th>
                      <th scope="col">Category</th>
                      <th scope="col">Price</th>
                      <th scope="col">Type</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((item, index) => {
                      const absoluteIndex = pageStart + index;
                      const title = item?.product_name || "Untitled lot";
                      const image = sanitizeImageUrl(item?.product_image);
                      return (
                        <tr key={`${item?.product_id || absoluteIndex}`}>
                          <td>
                            <div className="dd-fav-vehicle">
                              <span
                                className="dd-fav-vehicle__thumb"
                                aria-hidden
                              >
                                {image ? (
                                  <img src={image} alt="" />
                                ) : (
                                  <span className="dd-fav-vehicle__placeholder">
                                    —
                                  </span>
                                )}
                              </span>
                              <div className="dd-fav-vehicle__copy">
                                <strong>{title}</strong>
                                {item?.product_description ? (
                                  <span>{item.product_description}</span>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>{item?.product_category || "—"}</td>
                          <td>{formatMoney(item?.product_price)}</td>
                          <td>
                            <span
                              className={`dd-chip ${
                                tab.key === "auction"
                                  ? "dd-chip--auction"
                                  : "dd-chip--buynow"
                              }`}
                            >
                              {tab.name}
                            </span>
                          </td>
                          <td>
                            <div className="dd-actions">
                              <button
                                type="button"
                                className="dd-action"
                                title="View details"
                                onClick={() => setViewingItem(item)}
                              >
                                <FiExternalLink size={14} aria-hidden />
                                <span>View</span>
                              </button>
                              <button
                                type="button"
                                className="dd-action dd-action--danger"
                                onClick={() =>
                                  setPendingRemove({
                                    product_id: item?.product_id,
                                    name: title,
                                    index: absoluteIndex,
                                  })
                                }
                              >
                                <MdDeleteForever size={15} aria-hidden />
                                <span>Remove</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredList.length > pageSize ? (
                <div className="dd-pagination">
                  <PaginationControl
                    page={safePage}
                    between={3}
                    total={filteredList.length}
                    limit={pageSize}
                    changePage={(next) => setPage(next)}
                    ellipsis={1}
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      {pendingRemove ? (
        <ConfirmDialog
          open
          title="Remove from watchlist?"
          message={
            <p>
              Remove <strong>{pendingRemove.name}</strong> from your active{" "}
              {tab.name.toLowerCase()} watchlist.
            </p>
          }
          confirmLabel="Remove"
          confirming={removing}
          confirmingLabel="Removing…"
          onCancel={() => (!removing ? setPendingRemove(null) : null)}
          onConfirm={confirmRemove}
        />
      ) : null}

      <DetailDrawer
        item={viewingItem}
        eyebrow={`Watchlist · ${tab.name}`}
        onClose={() => setViewingItem(null)}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user_id: state.auth.id,
  wishList: state.product.wishList,
  wishListbuy: state.product.wishListbuy,
  loadingGetWishList: state.product.loadingGetWishList,
  loadingGetWishListBuy: state.product.loadingGetWishListBuy,
});

const mapDispatchToProps = (dispatch) => ({
  getWishList: (userId, typeQuery, actionType) =>
    dispatch({
      type: actionType,
      payload: {
        type: actionType,
        method: GET,
        label: `${Labels.get_wish_list}${userId}/?${typeQuery}`,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
  deleteItem: (data) =>
    dispatch({
      type: DELETE_WISH_LIST_ITEM,
      payload: {
        type: DELETE_WISH_LIST_ITEM,
        data,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(WatchlistActive);
